import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Form, Icon, Message } from "semantic-ui-react";
import { formatClock, playbackUrl, queryTracks, saveCatalogAlbum } from "./catalog";
import HomePlayer from "./HomePlayer";
import { usePodcast } from "./PlayerContext";
import { useAuthContext } from "../Hooks/useAuthContext";
import MediaUploadField from "../components/Uploader/MediaUploadField";
import "./podcast.css";

const PAGE_SIZE = 20;
const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "popular", label: "Popular" },
  { key: "liked", label: "Most liked" },
  { key: "rating", label: "Rating" },
  { key: "az", label: "A–Z" },
];

function Cover({ src, className }) {
  if (src) return <img className={className} src={src} alt="" />;
  return (
    <span className={`${className} is-empty`}>
      <Icon name="microphone" />
    </span>
  );
}

function Shelf({ kicker, title, empty, children }) {
  return (
    <section className="pod-shelf">
      {kicker && <p className="pod-kicker">{kicker}</p>}
      <h3>{title}</h3>
      {children || <p className="pod-shelf__empty">{empty}</p>}
    </section>
  );
}

function MediaCard({ src, title, meta, onClick, wide }) {
  return (
    <button type="button" className={wide ? "pod-shelf__card is-wide" : "pod-shelf__card"} onClick={onClick}>
      <span className="pod-shelf__thumb">
        <Cover src={src} className="" />
        {!wide && (
          <span className="pod-shelf__play" aria-hidden="true">
            <Icon name="play" />
          </span>
        )}
      </span>
      <strong>{title}</strong>
      {meta ? <small>{meta}</small> : null}
    </button>
  );
}

function EpisodeRow({ item, onPlay, onDelete, onEdit, canEdit = false, canDelete = false, deleting = false }) {
  return (
    <div className="pod-row">
      <button type="button" className="pod-row__main" onClick={() => onPlay(item)}>
        <Cover src={item.posterUrl || item.albumCoverUrl} className="pod-row__art" />
        <span className="pod-row__copy">
          <strong>{item.title}</strong>
          <small>
            {item.mood}
            {item.duration ? ` · ${formatClock(item.duration)}` : ""}
            {item.albumTitle ? ` · ${item.albumTitle}` : ""}
          </small>
        </span>
        <span className="pod-row__plays"><Icon name="headphones" /> {item.listens || 0}</span>
        <span className="pod-playdot" aria-hidden="true"><Icon name="play" /></span>
      </button>
      {(canEdit || canDelete) && <span className="pod-row__actions">
        {canEdit && <button type="button" className="pod-row__edit" onClick={() => onEdit(item)} aria-label={`Edit ${item.title}`} title="Edit episode"><Icon name="edit outline" /></button>}
        {canDelete && <button type="button" className="pod-row__delete" onClick={() => onDelete(item)} disabled={deleting} aria-label={`Delete ${item.title}`} title="Delete episode">
          <Icon name={deleting ? "spinner" : "trash alternate outline"} />
        </button>}
      </span>}
    </div>
  );
}

export default function PodcastLibrary({ isHomepage = false }) {
  const podcast = usePodcast();
  const { user } = useAuthContext();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [mood, setMood] = useState("All");
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [album, setAlbum] = useState(null);
  const [episodeTitle, setEpisodeTitle] = useState("");
  const [episodeTranscript, setEpisodeTranscript] = useState("");
  const [episodeDescription, setEpisodeDescription] = useState("");
  const [episodeGenre, setEpisodeGenre] = useState("");
  const [episodeAudioUrl, setEpisodeAudioUrl] = useState("");
  const [episodeCoverUrl, setEpisodeCoverUrl] = useState("");
  const [episodeFeatured, setEpisodeFeatured] = useState(false);
  const [episodeAlbumId, setEpisodeAlbumId] = useState("");
  const [albumCreatorOpen, setAlbumCreatorOpen] = useState(false);
  const [newAlbumTitle, setNewAlbumTitle] = useState("");
  const [newAlbumCoverUrl, setNewAlbumCoverUrl] = useState("");
  const [albumSaving, setAlbumSaving] = useState(false);
  const [albumMessage, setAlbumMessage] = useState(null);
  const [albumManagerOpen, setAlbumManagerOpen] = useState(false);
  const [albumDraft, setAlbumDraft] = useState(null);
  const [albumEditSaving, setAlbumEditSaving] = useState(false);
  const [albumManagerMessage, setAlbumManagerMessage] = useState(null);
  const [editingEpisodeId, setEditingEpisodeId] = useState(null);
  const [episodeFormOpen, setEpisodeFormOpen] = useState(false);
  const [episodeBusy, setEpisodeBusy] = useState({});
  const [episodeSaving, setEpisodeSaving] = useState(false);
  const [episodeMessage, setEpisodeMessage] = useState(null);
  const [deletingIds, setDeletingIds] = useState([]);
  const [deleteMessage, setDeleteMessage] = useState("");
  const lastRefreshAt = useRef(0);
  const canManagePodcast = ["1", "2", "3"].includes(String(user?.user?.role));
  const onEpisodeUploadBusy = useCallback((kind, busy) => setEpisodeBusy((current) => ({ ...current, [kind]: busy })), []);
  const episodeUploading = Object.values(episodeBusy).some(Boolean);

  const refresh = podcast.refresh;
  const refreshOnFocus = useCallback(() => {
    if (document.visibilityState === "hidden") return;
    const now = Date.now();
    if (now - lastRefreshAt.current < 1500) return;
    lastRefreshAt.current = now;
    refresh();
  }, [refresh]);
  useEffect(() => {
    refreshOnFocus();
    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [refreshOnFocus]);

  const moods = useMemo(() => {
    const values = Array.from(new Set(podcast.tracks.map((track) => track.mood).filter(Boolean)));
    values.sort((a, b) => a.localeCompare(b));
    return ["All", ...values];
  }, [podcast.tracks]);

  const filtered = useMemo(
    () => queryTracks(podcast.tracks, {
      search,
      sortBy,
      mood: mood === "All" ? undefined : mood,
    }),
    [mood, podcast.tracks, search, sortBy]
  );
  const featured = useMemo(
    () => podcast.tracks.filter((track) => track.isFeatured).slice(0, 12),
    [podcast.tracks]
  );
  const videos = useMemo(
    () => podcast.tracks.filter((track) => track.videoUrl || track.youtubeUrl).slice(0, 12),
    [podcast.tracks]
  );
  const shown = filtered.slice(0, visible);
  const filterCount = (mood !== "All" ? 1 : 0) + (sortBy !== "newest" ? 1 : 0);

  const openTrack = async (track, list) => {
    const queue = (list || filtered).filter((item) => playbackUrl(item));
    const index = Math.max(0, queue.findIndex((item) => item._id === track._id));
    if (!playbackUrl(track) && track.youtubeUrl) {
      window.open(track.youtubeUrl, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      await podcast.playQueue(queue.length ? queue : [track], index);
    } catch (playError) {
      window.alert(playError.message || "This episode could not be started.");
    }
  };

  const albumTracks = album
    ? queryTracks(podcast.tracks, { albumId: album._id, sortBy: "oldest" })
    : [];

  const clearEpisodeForm = () => {
    setEditingEpisodeId(null);
    setEpisodeTitle("");
    setEpisodeTranscript("");
    setEpisodeDescription("");
    setEpisodeGenre("");
    setEpisodeAudioUrl("");
    setEpisodeCoverUrl("");
    setEpisodeFeatured(false);
    setEpisodeAlbumId("");
  };

  const editEpisode = (item) => {
    setEpisodeFormOpen(true);
    setEditingEpisodeId(item._id);
    setEpisodeTitle(item.title || "");
    setEpisodeTranscript(item.lyrics || "");
    setEpisodeDescription(item.description || "");
    setEpisodeGenre(item.mood || "");
    setEpisodeAudioUrl(item.audioUrl || "");
    setEpisodeCoverUrl(item.posterUrl || "");
    setEpisodeFeatured(Boolean(item.isFeatured));
    setEpisodeAlbumId(item.albumId || "");
    setEpisodeMessage(null);
    document.getElementById("pod-admin-upload-title")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const createAlbum = async () => {
    if (!newAlbumTitle.trim() || albumSaving || episodeUploading) return;
    setAlbumSaving(true);
    setAlbumMessage(null);
    try {
      const album = await saveCatalogAlbum({
        _id: `alb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        title: newAlbumTitle.trim(),
        coverUrl: newAlbumCoverUrl,
      }, user);
      setNewAlbumTitle("");
      setNewAlbumCoverUrl("");
      setAlbumCreatorOpen(false);
      setEpisodeAlbumId(album._id);
      await podcast.refresh();
    } catch (error) {
      setAlbumMessage({ error: true, text: error.message || "Album could not be created." });
    } finally {
      setAlbumSaving(false);
    }
  };

  const beginAlbumEdit = (item) => {
    setAlbumDraft({ _id: String(item._id), title: item.title || "", coverUrl: item.coverUrl || "" });
    setAlbumManagerMessage(null);
  };

  const saveAlbumEdit = async () => {
    if (!albumDraft?.title.trim() || albumEditSaving || episodeUploading) return;
    setAlbumEditSaving(true);
    setAlbumManagerMessage(null);
    try {
      if (/^[a-f\d]{24}$/i.test(albumDraft._id)) {
        const response = await fetch(`/api/albums/${albumDraft._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
          body: JSON.stringify({ title: albumDraft.title.trim(), coverUrl: albumDraft.coverUrl || "", userId: user.user._id, role: String(user.user.role) }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.message || "Album could not be updated.");
      }
      await saveCatalogAlbum({ ...albumDraft, title: albumDraft.title.trim() }, user);
      await podcast.refresh();
      setAlbumDraft(null);
      setAlbumManagerMessage({ text: "Album updated." });
    } catch (error) {
      setAlbumManagerMessage({ error: true, text: error.message || "Album could not be updated." });
    } finally {
      setAlbumEditSaving(false);
    }
  };

  const publishEpisode = async (event) => {
    event.preventDefault();
    if (episodeUploading || episodeSaving) return;
    if (!episodeTitle.trim() || !episodeTranscript.trim() || !episodeDescription.trim() || !episodeAudioUrl) {
      setEpisodeMessage({ error: true, text: "Enter a title, transcript, and description, then upload an audio file." });
      return;
    }
    setEpisodeSaving(true);
    setEpisodeMessage(null);
    try {
      const editing = Boolean(editingEpisodeId);
      const selectedAlbum = podcast.albums.find((item) => String(item._id) === String(episodeAlbumId));
      if (selectedAlbum) await saveCatalogAlbum(selectedAlbum, user);
      const response = await fetch(editing ? `/api/posts/${editingEpisodeId}` : "/api/posts", {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({
          username: user.user.username,
          userId: user.user._id,
          role: String(user.user.role),
          body: episodeTranscript.trim(),
          description: episodeDescription.trim(),
          topic: episodeTitle.trim(),
          category: "Audio",
          subCategory: episodeGenre.trim() || "Markets",
          audioUrl: episodeAudioUrl,
          thumbnailUrl: episodeCoverUrl || undefined,
          isFeatured: episodeFeatured,
          albumId: episodeAlbumId,
          ...(editing ? { isAdmin: true, userRole: Number(user.user.role) } : {}),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || result.error || "Episode could not be published.");
      await podcast.refresh();
      clearEpisodeForm();
      setEpisodeFormOpen(false);
      setEpisodeMessage({ text: editing ? "Episode updated." : "Episode published." });
    } catch (error) {
      setEpisodeMessage({ error: true, text: error.message });
    } finally {
      setEpisodeSaving(false);
    }
  };

  const deleteEpisode = async (item) => {
    if (!canManagePodcast || !user?.user?._id) return;
    if (!window.confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    setDeleteMessage("");
    setDeletingIds((ids) => [...ids, item._id]);
    try {
      const response = await fetch(`/api/posts/${item._id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ userId: user.user._id, isAdmin: true, userRole: Number(user.user.role) }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error || result?.message || "Episode could not be deleted.");
      await podcast.refresh();
    } catch (error) {
      setDeleteMessage(error.message || "Episode could not be deleted.");
    } finally {
      setDeletingIds((ids) => ids.filter((id) => id !== item._id));
    }
  };

  return (
    <section className="pod pod-home">
      <HomePlayer episodesHref={isHomepage ? "/podcast#pod-episodes" : "#pod-episodes"} compact={isHomepage} />
      {!isHomepage && canManagePodcast && (
        <section className="pod-admin-upload" aria-labelledby="pod-admin-upload-title">
          <div className="pod-admin-upload__head">
            <h2 id="pod-admin-upload-title">{editingEpisodeId ? "Edit podcast episode" : "Podcast episodes"}</h2>
            {!episodeFormOpen ? (
              <Button type="button" primary onClick={() => { setEpisodeMessage(null); setEpisodeFormOpen(true); }}>
                <Icon name="plus" /> Add new podcast
              </Button>
            ) : !editingEpisodeId && (
              <Button type="button" onClick={() => { setEpisodeFormOpen(false); clearEpisodeForm(); setEpisodeMessage(null); }}>Cancel</Button>
            )}
          </div>
          {episodeMessage && !episodeFormOpen && <Message positive={!episodeMessage.error} negative={episodeMessage.error} content={episodeMessage.text} />}
          {episodeFormOpen && <form onSubmit={publishEpisode}>
            <Form.Input label="Episode title" value={episodeTitle} onChange={(event) => setEpisodeTitle(event.target.value)} maxLength={180} required />
            <Form.TextArea label="Transcript" value={episodeTranscript} onChange={(event) => setEpisodeTranscript(event.target.value)} maxLength={50000} required />
            <Form.TextArea label="Description" value={episodeDescription} onChange={(event) => setEpisodeDescription(event.target.value)} maxLength={10000} required />
            <Form.Input label="Topic or genre" value={episodeGenre} onChange={(event) => setEpisodeGenre(event.target.value)} maxLength={80} placeholder="Markets" />
            <Form.Select
              label="Album"
              placeholder={podcast.albums.length ? "Choose an album" : "No albums available"}
              options={[{ key: "none", value: "", text: "No album" }, ...podcast.albums.map((item) => ({ key: item._id, value: item._id, text: item.title }))]}
              value={episodeAlbumId}
              onChange={(_, data) => setEpisodeAlbumId(data.value)}
              disabled={!podcast.albums.length}
            />
            <div className="pod-admin-upload__album-actions">
              <Button type="button" size="small" onClick={() => { setAlbumCreatorOpen((open) => !open); setAlbumMessage(null); }}>
                <Icon name={albumCreatorOpen ? "minus" : "plus"} /> {albumCreatorOpen ? "Cancel new album" : "Create album"}
              </Button>
            </div>
            {albumCreatorOpen && <div className="pod-admin-upload__new-album">
              <Form.Input label="New album name" value={newAlbumTitle} onChange={(event) => setNewAlbumTitle(event.target.value)} maxLength={120} />
              <MediaUploadField key="new-album-cover" kind="image" onUploaded={setNewAlbumCoverUrl} onBusyChange={onEpisodeUploadBusy} />
              <Button type="button" onClick={createAlbum} disabled={!newAlbumTitle.trim() || albumSaving || episodeUploading} loading={albumSaving}>Save album</Button>
            </div>}
            {albumMessage && <Message negative content={albumMessage.text} />}
            <MediaUploadField key={`audio-${editingEpisodeId || "new"}`} kind="audio" onUploaded={setEpisodeAudioUrl} onBusyChange={onEpisodeUploadBusy} />
            <MediaUploadField key={`image-${editingEpisodeId || "new"}`} kind="image" onUploaded={setEpisodeCoverUrl} onBusyChange={onEpisodeUploadBusy} />
            <Checkbox label="Curated pick (show in Featured)" checked={episodeFeatured} onChange={(_, data) => setEpisodeFeatured(data.checked)} />
            {episodeMessage && episodeFormOpen && <Message positive={!episodeMessage.error} negative={episodeMessage.error} content={episodeMessage.text} />}
            <div className="pod-admin-upload__actions">
            <Button type="submit" primary disabled={episodeUploading || episodeSaving || !episodeAudioUrl} loading={episodeSaving}>
              {episodeSaving ? (editingEpisodeId ? "Saving…" : "Publishing…") : (editingEpisodeId ? "Save changes" : "Publish episode")}
            </Button>
            {editingEpisodeId && <Button type="button" onClick={() => { clearEpisodeForm(); setEpisodeFormOpen(false); setEpisodeMessage(null); }}>Cancel edit</Button>}
            </div>
          </form>}
        </section>
      )}
      {!isHomepage && canManagePodcast && <section className="pod-admin-albums">
        <div className="pod-admin-albums__head">
          <div><h2>Podcast albums</h2><p>Edit album names and cover images.</p></div>
          <Button type="button" onClick={() => { const nextOpen = !albumManagerOpen; setAlbumManagerOpen(nextOpen); setAlbumDraft(null); setAlbumManagerMessage(null); if (nextOpen) refresh(); }}>
            <Icon name={albumManagerOpen ? "chevron up" : "edit outline"} /> {albumManagerOpen ? "Close" : "Manage albums"}
          </Button>
        </div>
        {albumManagerOpen && <>
          {albumManagerMessage && <Message positive={!albumManagerMessage.error} negative={albumManagerMessage.error} content={albumManagerMessage.text} />}
          {podcast.albums.length ? <div className="pod-admin-albums__list">
            {podcast.albums.map((item) => <div className="pod-admin-albums__item" key={item._id}>
              {item.coverUrl ? <img src={item.coverUrl} alt="" /> : <span className="pod-admin-albums__fallback"><Icon name="music" /></span>}
              <div className="pod-admin-albums__copy"><strong>{item.title}</strong><small>{podcast.tracks.filter((track) => track.albumId === String(item._id)).length} episodes</small></div>
              <Button type="button" size="small" onClick={() => beginAlbumEdit(item)}><Icon name="edit" /> Edit</Button>
            </div>)}
          </div> : <p className="pod-empty">No albums yet. Create one while adding a podcast episode.</p>}
          {albumDraft && <div className="pod-admin-albums__editor">
            <h3>Edit album</h3>
            <Form.Input label="Album name" value={albumDraft.title} onChange={(event) => setAlbumDraft((draft) => ({ ...draft, title: event.target.value }))} maxLength={120} required />
            {albumDraft.coverUrl && <img className="pod-admin-albums__preview" src={albumDraft.coverUrl} alt="Album cover preview" />}
            <MediaUploadField key={`edit-album-${albumDraft._id}`} kind="image" onUploaded={(coverUrl) => setAlbumDraft((draft) => ({ ...draft, coverUrl }))} onBusyChange={onEpisodeUploadBusy} />
            <div className="pod-admin-upload__actions">
              <Button type="button" primary onClick={saveAlbumEdit} disabled={!albumDraft.title.trim() || albumEditSaving || episodeUploading} loading={albumEditSaving}>Save album</Button>
              <Button type="button" onClick={() => setAlbumDraft(null)}>Cancel</Button>
            </div>
          </div>}
        </>}
      </section>}
      {!isHomepage && <div className="pod-search">
          <Icon name="search" />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setVisible(PAGE_SIZE);
            }}
            placeholder="Search episodes"
            aria-label="Search episodes"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} aria-label="Clear search">
              <Icon name="close" />
            </button>
          )}
          <button
            type="button"
            className={filterCount ? "is-on" : ""}
            onClick={() => setFilterOpen(true)}
            aria-label="Filter episodes"
          >
            <Icon name="options" />
            {filterCount > 0 && <em>{filterCount}</em>}
          </button>
        </div>}

      {podcast.error && (
        <div className="pod-empty">
          <p>{podcast.error}</p>
          <button type="button" onClick={podcast.refresh}>Try again</button>
        </div>
      )}

      {!search && (
        <div className={`pod-home__rails${isHomepage ? " pod-home__rails--homepage" : ""}`}>
          {podcast.albums.length > 0 && <Shelf title="Albums">
              <div className="pod-shelf__rail">
                {podcast.albums.map((item) => (
                  <MediaCard
                    key={item._id}
                    wide={podcast.albums.length === 1}
                    src={item.coverUrl}
                    title={item.title}
                    meta={`${item.count} episode${item.count === 1 ? "" : "s"}`}
                    onClick={() => setAlbum(item)}
                  />
                ))}
              </div>
          </Shelf>}
          {featured.length > 0 && <Shelf title="Featured">
              <div className="pod-shelf__rail">
                {featured.map((item) => (
                  <MediaCard
                    key={item._id}
                    src={item.posterUrl || item.albumCoverUrl}
                    title={item.title}
                    meta={item.mood}
                    onClick={() => openTrack(item, featured)}
                  />
                ))}
              </div>
          </Shelf>}
          {!isHomepage && mood === "All" && podcast.recent.length > 0 && <Shelf title="Recently played">
            <div className="pod-shelf__rail">
                {podcast.recent.map((item) => (
                  <MediaCard
                    key={item._id}
                    src={item.posterUrl || item.albumCoverUrl}
                    title={item.title}
                    meta={item.albumTitle || item.mood}
                    onClick={() => openTrack(item, podcast.recent)}
                  />
                ))}
            </div>
          </Shelf>}
        </div>
      )}

      {!search && videos.length > 0 && (
        <section className="pod-block">
          <div className="pod-block__head">
            <div>
              <h2>Videos</h2>
            </div>
          </div>
          <div className="pod-rail">
            {videos.map((item) => (
              <button type="button" className="pod-card" key={item._id} onClick={() => openTrack(item, videos)}>
                <Cover src={item.posterUrl || item.albumCoverUrl} className="pod-card__art" />
                <strong>{item.title}</strong>
              </button>
            ))}
          </div>
        </section>
      )}

      {!isHomepage && <>
      <div className="pod-block__head" id="pod-episodes">
        <div>
          <h2>All episodes</h2>
        </div>
        <span>{filtered.length}</span>
      </div>
      {podcast.loading && !podcast.tracks.length ? (
        <p className="pod-empty">Loading episodes…</p>
      ) : shown.length ? (
        <div className="pod-list">
          {shown.map((item) => (
            <EpisodeRow key={item._id} item={item} onPlay={(track) => openTrack(track, filtered)} canEdit={canManagePodcast} onEdit={editEpisode} canDelete={canManagePodcast} deleting={deletingIds.includes(item._id)} onDelete={deleteEpisode} />
          ))}
        </div>
      ) : (
        !podcast.error && <p className="pod-empty">No episodes yet</p>
      )}
      {deleteMessage && <Message negative content={deleteMessage} />}
      {canManagePodcast && podcast.testTracks.length > 0 && <section className="pod-admin-tests">
        <div className="pod-block__head"><div><h2>Test episodes</h2></div><span>{podcast.testTracks.length}</span></div>
        <div className="pod-list">
          {podcast.testTracks.map((item) => <EpisodeRow key={item._id} item={item} onPlay={(track) => openTrack(track, podcast.testTracks)} canEdit onEdit={editEpisode} canDelete deleting={deletingIds.includes(item._id)} onDelete={deleteEpisode} />)}
        </div>
      </section>}
      {visible < filtered.length && (
        <button type="button" className="pod-more" onClick={() => setVisible((count) => count + PAGE_SIZE)}>
          Load more
        </button>
      )}

      </>}

      {!search && podcast.saved.length > 0 && (
        <section className="pod-block pod-saved">
          <div className="pod-block__head">
            <div>
              <h2>Saved episodes</h2>
            </div>
          </div>
          <div className="pod-rail">
            {podcast.saved.map((item) => (
              <button type="button" className="pod-card" key={item._id} onClick={() => openTrack(item, podcast.saved)}>
                <Cover src={item.posterUrl || item.albumCoverUrl} className="pod-card__art" />
                <strong>{item.title}</strong>
              </button>
            ))}
          </div>
        </section>
      )}

      {filterOpen && (
        <div className="pod-sheet" onClick={() => setFilterOpen(false)}>
          <div className="pod-sheet__card" onClick={(event) => event.stopPropagation()}>
            <div className="pod-sheet__head">
              <h3>Filter episodes</h3>
              <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close">
                <Icon name="close" />
              </button>
            </div>
            <p>Sort</p>
            <div className="pod-chips">
              {SORTS.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  className={sortBy === option.key ? "is-on" : ""}
                  onClick={() => setSortBy(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p>Topic</p>
            <div className="pod-chips">
              {moods.map((option) => (
                <button
                  type="button"
                  key={option}
                  className={mood === option ? "is-on" : ""}
                  onClick={() => setMood(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="pod-sheet__actions">
              <button
                type="button"
                onClick={() => {
                  setMood("All");
                  setSortBy("newest");
                }}
              >
                Reset
              </button>
              <button type="button" className="is-solid" onClick={() => setFilterOpen(false)}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {album && (
        <div className="pod-sheet" onClick={() => setAlbum(null)}>
          <div className="pod-sheet__card" onClick={(event) => event.stopPropagation()}>
            <div className="pod-sheet__head">
              <div>
                <p className="pod-kicker">Album</p>
                <h3>{album.title}</h3>
                <small>{albumTracks.length} episode{albumTracks.length === 1 ? "" : "s"}</small>
              </div>
              <button type="button" onClick={() => setAlbum(null)} aria-label="Close">
                <Icon name="close" />
              </button>
            </div>
            {albumTracks.length > 0 && (
              <button
                type="button"
                className="pod-more is-solid"
                onClick={() => {
                  const next = album;
                  setAlbum(null);
                  openTrack(albumTracks[0], albumTracks);
                  void next;
                }}
              >
                Play all
              </button>
            )}
            <div className="pod-list">
              {albumTracks.length ? albumTracks.map((item) => (
                <EpisodeRow
                  key={item._id}
                  item={item}
                  onPlay={(track) => {
                    setAlbum(null);
                    openTrack(track, albumTracks);
                  }}
                />
              )) : <p className="pod-empty">No episodes in this album yet</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
